import {
  CheckCircleIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { cn } from '@shadcn/lib/utils';
import { FileDown, FileWarning, Loader2, Upload, X } from 'lucide-react';
import { parse } from 'papaparse';
import { useState } from 'react';

const HELP_URL =
  'https://spellcaster.education/documentation/managing-students/adding-learners/';

export interface CsvRow {
  firstName: string;
  lastName: string;
  username?: string;
  password?: string;
}

interface ColumnMapping {
  firstName: {
    value: string;
    index: number;
  };
  lastName: {
    value: string;
    index: number;
  };
  username: {
    value: string | null;
    index: number;
  };
  password: {
    value: string | null;
    index: number;
  };
}

interface RowErrors {
  firstName: number;
  lastName: number;
}

interface CsvImportModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (rows: CsvRow[]) => void;
}

export default function CsvImportModal({
  open,
  onClose,
  onConfirm,
}: CsvImportModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importedRows, setImportedRows] = useState<CsvRow[]>([]);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    firstName: {
      value: '',
      index: -1,
    },
    lastName: {
      value: '',
      index: -1,
    },
    username: {
      value: null,
      index: -1,
    },
    password: {
      value: null,
      index: -1,
    },
  });
  const [rawCsvData, setRawCsvData] = useState<Record<string, string>[]>([]);
  const [rowErrors, setRowErrors] = useState<RowErrors>({
    firstName: 0,
    lastName: 0,
  });

  const [isDragging, setDragging] = useState(false);
  const [validatingStatus, setValidatingStatus] = useState<
    'idle' | 'validating' | 'success' | 'error'
  >('idle');
  const [firstNameColumnFound, setFirstNameColumnFound] = useState(false);
  const [lastNameColumnFound, setLastNameColumnFound] = useState(false);

  const downloadTemplate = () => {
    const templateData =
      'First Name,Last Name,Username,Password\nJohn,Doe,johndoe,hungrybear32\nJane,Doe,,\nMartin,James,,\nJim,Jam,,\nBill,Todd,,';
    const blob = new Blob([templateData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateCsvData = (file: File) => {
    setValidatingStatus('validating');
    setImportedRows([]);

    parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: function (results) {
        const { data, errors } = results;

        // Set available columns
        const columns =
          results.meta.fields?.filter((col) => col.trim() !== '') || [];
        setAvailableColumns(columns);

        // Set initial column mappings
        const initialMapping: ColumnMapping = {
          firstName: {
            value:
              columns.find((col) => col.toLowerCase().includes('first')) ||
              columns.find((col) => col.toLowerCase().includes('given')) ||
              '',
            index: (() => {
              const i = columns.findIndex((col) =>
                col.toLowerCase().includes('first'),
              );
              return i !== -1
                ? i
                : columns.findIndex((col) =>
                    col.toLowerCase().includes('given'),
                  );
            })(),
          },
          lastName: {
            value:
              columns.find((col) => col.toLowerCase().includes('last')) ||
              columns.find((col) => col.toLowerCase().includes('surname')) ||
              '',
            index: (() => {
              const i = columns.findIndex((col) =>
                col.toLowerCase().includes('last'),
              );
              return i !== -1
                ? i
                : columns.findIndex((col) =>
                    col.toLowerCase().includes('surname'),
                  );
            })(),
          },
          username: {
            value:
              columns.find((col) => col.toLowerCase().includes('user')) || '',
            index: columns.findIndex((col) =>
              col.toLowerCase().includes('user'),
            ),
          },
          password: {
            value:
              columns.find((col) => col.toLowerCase().includes('pass')) || '',
            index: columns.findIndex((col) =>
              col.toLowerCase().includes('pass'),
            ),
          },
        };
        setColumnMapping(initialMapping);

        // Store raw data for later reprocessing
        setRawCsvData(data);

        // Update column found states
        setFirstNameColumnFound(initialMapping.firstName.index > -1);
        setLastNameColumnFound(initialMapping.lastName.index > -1);

        // Validate required columns
        let validationErrors = 0;
        if (initialMapping.firstName.index === -1) {
          validationErrors++;
        }
        if (initialMapping.lastName.index === -1) {
          validationErrors++;
        }

        if (errors.length > 0 || validationErrors > 0 || data.length === 0) {
          setValidatingStatus('error');
        } else {
          setValidatingStatus('success');
          processRowsWithMapping(data, initialMapping);
        }
      },
    });
  };

  const processRowsWithMapping = (
    data: Record<string, string>[],
    mapping: ColumnMapping,
  ) => {
    const errors: RowErrors = {
      firstName: 0,
      lastName: 0,
    };
    const processedRows: CsvRow[] = data.map((row) => {
      const firstNameIndex = mapping.firstName.index;
      const lastNameIndex = mapping.lastName.index;
      const usernameIndex = mapping.username.index;
      const passwordIndex = mapping.password.index;
      const rowValues = Object.values(row);

      if (firstNameIndex === -1 || !rowValues[firstNameIndex]) {
        errors.firstName++;
      }
      if (lastNameIndex === -1 || !rowValues[lastNameIndex]) {
        errors.lastName++;
      }

      return {
        firstName: rowValues[firstNameIndex] || '',
        lastName: rowValues[lastNameIndex] || '',
        username:
          usernameIndex !== -1 && rowValues[usernameIndex] !== 'None'
            ? rowValues[usernameIndex]
            : undefined,
        password:
          passwordIndex !== -1 && rowValues[passwordIndex] !== 'None'
            ? rowValues[passwordIndex]
            : undefined,
      };
    });

    if (errors.firstName > 0 || errors.lastName > 0) {
      setValidatingStatus('error');
    }

    setRowErrors(errors);
    setImportedRows(processedRows);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);

    if (e.dataTransfer.files?.length) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        validateCsvData(file);
      } else {
        setValidatingStatus('error');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      setSelectedFile(file);
      validateCsvData(file);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedFile(null);
      setValidatingStatus('idle');
      onClose();
    }
  };

  const handleColumnMappingChange = (
    field: keyof ColumnMapping,
    value: string,
  ) => {
    const newMapping = {
      ...columnMapping,
      [field]: {
        value: value === 'None' ? null : value,
        index: availableColumns.indexOf(value),
      },
    };
    setColumnMapping(newMapping);

    // Update column found states
    if (field === 'firstName')
      setFirstNameColumnFound(availableColumns.indexOf(value) > -1);
    if (field === 'lastName')
      setLastNameColumnFound(availableColumns.indexOf(value) > -1);

    // Validate required columns
    const validationErrors: string[] = [];
    if (newMapping.firstName.index === -1) {
      validationErrors.push('First Name column is required');
    }
    if (newMapping.lastName.index === -1) {
      validationErrors.push('Last Name column is required');
    }

    // Update validation status
    if (validationErrors.length > 0) {
      setValidatingStatus('error');
    } else {
      setValidatingStatus('success');
    }

    // Reprocess rows with new mapping
    if (rawCsvData.length > 0) {
      processRowsWithMapping(rawCsvData, newMapping);
    }
  };

  const renderColumnMapping = () => (
    <div className="flex flex-col gap-2 w-full border border-border rounded-md px-4 py-2">
      <span
        className={cn(
          'flex items-center gap-2 text-sm font-medium',
          firstNameColumnFound && lastNameColumnFound
            ? 'text-foreground'
            : 'text-destructive',
        )}
      >
        Columns
        {firstNameColumnFound && lastNameColumnFound ? (
          <CheckCircleIcon className="h-4 w-4 text-green-600" />
        ) : (
          <WarningIcon className="h-4 w-4" />
        )}
      </span>
      <div className="grid grid-cols-2 gap-2 items-center mt-2">
        <span className="text-xs text-muted-foreground">Field</span>
        <span className="text-xs text-muted-foreground">CSV Column</span>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">First Name</span>
          {firstNameColumnFound ? (
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          ) : (
            <XCircleIcon className="h-4 w-4 text-destructive" />
          )}
        </div>
        <Select
          value={columnMapping.firstName.value || ''}
          onValueChange={(value) =>
            handleColumnMappingChange('firstName', value)
          }
        >
          <SelectTrigger
            className={cn(
              'w-full',
              !firstNameColumnFound && 'border-destructive',
            )}
          >
            <SelectValue placeholder="Select column" />
          </SelectTrigger>
          <SelectContent>
            {availableColumns.map((col) => (
              <SelectItem key={col} value={col}>
                {col}
              </SelectItem>
            ))}
            {availableColumns.length === 0 && (
              <div className="text-sm text-muted-foreground w-full text-center py-1">
                No column headers found
              </div>
            )}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">Last Name</span>
          {lastNameColumnFound ? (
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          ) : (
            <XCircleIcon className="h-4 w-4 text-destructive" />
          )}
        </div>
        <Select
          value={columnMapping.lastName.value || ''}
          onValueChange={(value) =>
            handleColumnMappingChange('lastName', value)
          }
        >
          <SelectTrigger
            className={cn(
              'w-full',
              !lastNameColumnFound && 'border-destructive',
            )}
          >
            <SelectValue placeholder="Select column" />
          </SelectTrigger>
          <SelectContent>
            {availableColumns.map((col) => (
              <SelectItem key={col} value={col}>
                {col}
              </SelectItem>
            ))}
            {availableColumns.length === 0 && (
              <div className="text-sm text-muted-foreground w-full text-center py-1">
                No column headers found
              </div>
            )}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">
            Username{' '}
            <span className="text-xs text-muted-foreground">(optional)</span>
          </span>
          {columnMapping.username.value && (
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          )}
        </div>
        <Select
          value={columnMapping.username.value || ''}
          onValueChange={(value) =>
            handleColumnMappingChange('username', value)
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select column" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="None">None</SelectItem>
            {availableColumns.map((col) => (
              <SelectItem key={col} value={col}>
                {col}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">
            Password{' '}
            <span className="text-xs text-muted-foreground">(optional)</span>
          </span>
          {columnMapping.password.value && (
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          )}
        </div>
        <Select
          value={columnMapping.password.value || ''}
          onValueChange={(value) =>
            handleColumnMappingChange('password', value)
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select column" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="None">None</SelectItem>
            {availableColumns.map((col) => (
              <SelectItem key={col} value={col}>
                {col}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Learner Details</DialogTitle>
          <DialogDescription>
            Upload a CSV file with learner information. Column headers must be
            present. First and last names are required.
            <Button
              variant="link"
              size="sm"
              className="p-0 text-sm gap-1"
              onClick={() => window.open(HELP_URL, '_blank')}
            >
              Learn more about CSV imports.
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'group my-2 relative border rounded-md transition-colors duration-200 border-muted-foreground/25',
            isDragging && 'border-primary bg-accent',
            selectedFile
              ? 'border-border'
              : 'border-2 border-dashed hover:border-primary hover:bg-accent cursor-pointer',
          )}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
        >
          <input
            id="fileInput"
            type="file"
            className="hidden"
            accept=".csv"
            onChange={handleFileSelect}
          />
          {selectedFile ? (
            <div className="flex flex-col px-4 py-3 gap-4 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn(
                    'flex items-center gap-2 font-medium',
                    validatingStatus === 'success'
                      ? 'text-green-600'
                      : 'text-destructive',
                  )}
                >
                  {validatingStatus === 'success' ? (
                    <CheckCircleIcon className="h-4 w-4" />
                  ) : (
                    <FileWarning className="h-4 w-4" />
                  )}
                  {selectedFile.name}
                </span>
                <X
                  className="h-4 w-4 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setValidatingStatus('idle');
                    setColumnMapping({
                      firstName: {
                        value: '',
                        index: -1,
                      },
                      lastName: {
                        value: '',
                        index: -1,
                      },
                      username: { value: null, index: -1 },
                      password: { value: null, index: -1 },
                    });
                    setAvailableColumns([]);
                    setImportedRows([]);
                  }}
                />
              </div>

              {renderColumnMapping()}

              <div className="text-destructive text-sm border border-border rounded-md px-4 py-2 flex flex-col gap-2">
                <span
                  className={cn(
                    'flex items-center gap-2 text-sm font-medium',
                    rowErrors.firstName > 0 ||
                      rowErrors.lastName > 0 ||
                      rawCsvData.length - 1 <= 0
                      ? 'text-destructive'
                      : 'text-foreground',
                  )}
                >
                  Rows
                  {rowErrors.firstName > 0 ||
                  rowErrors.lastName > 0 ||
                  rawCsvData.length - 1 <= 0 ? (
                    <WarningIcon className="h-4 w-4" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                  )}
                </span>

                <span className="text-sm text-muted-foreground">
                  Found {importedRows.length}{' '}
                  {importedRows.length === 1 ? 'row' : 'rows'}
                </span>

                <ul className="list-disc list-inside ml-2 mx-0">
                  {rowErrors.firstName > 0 && (
                    <li>{rowErrors.firstName} rows have no first name.</li>
                  )}
                  {rowErrors.lastName > 0 && (
                    <li>{rowErrors.lastName} rows have no last name.</li>
                  )}
                </ul>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center p-6 text-center gap-4"
              onClick={() => document.getElementById('fileInput')?.click()}
            >
              <div
                className={cn(
                  'rounded-full bg-muted group-hover:bg-white transition-colors duration-200 p-2.5',
                  isDragging && 'bg-white',
                )}
              >
                <Upload className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  Drop your file here or click to upload
                </span>
                <span className="text-xs text-muted-foreground">
                  CSV files only
                </span>
              </div>
            </div>
          )}

          {validatingStatus === 'validating' && (
            <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Validating...</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            className="mr-auto"
            onClick={downloadTemplate}
          >
            <FileDown className="mr-2 h-4 w-4" />
            Download Template
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(importedRows)}
            disabled={
              !selectedFile ||
              importedRows.length === 0 ||
              validatingStatus !== 'success' ||
              !firstNameColumnFound ||
              !lastNameColumnFound ||
              rowErrors.firstName > 0 ||
              rowErrors.lastName > 0
            }
          >
            Import Learners
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
